import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Home, 
  CreditCard,
  ChevronRight,
  TestTubes,
  AlertCircle,
  Download,
  FileText,
  Search,
  Edit,
  Trash2,
  X,
  MoreVertical,
  Loader2,
  Building2,
  Copy,
  CheckCircle2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [tests, setTests] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/bank-transfer-details`);
      setBankDetails(response.data);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const [bookingsRes, testsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/tests`)
      ]);
      setBookings(bookingsRes.data);
      
      // Create a map of test IDs to test details
      const testsMap = {};
      testsRes.data.forEach(test => {
        testsMap[test.id] = test;
      });
      setTests(testsMap);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = (booking) => {
    setSelectedBooking(booking);
    setSelectedPaymentMethod('');
    setPaymentDialogOpen(true);
  };

  const processPayment = async () => {
    if (!selectedBooking || !selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setPaymentProcessing(true);
    try {
      if (selectedPaymentMethod === 'bank_transfer') {
        // For bank transfer, just show the details and close dialog
        toast.success('Please transfer to the bank account shown. Contact support after payment.');
        setPaymentDialogOpen(false);
      } else {
        // For Paystack or other online payments, redirect to checkout
        const response = await axios.post(
          `${API}/payments/create-checkout?booking_id=${selectedBooking.id}&gateway=${selectedPaymentMethod}&origin_url=${window.location.origin}`
        );
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleDownloadReceipt = async (bookingId) => {
    setDownloadingReceipt(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/receipt`, {
        responseType: 'blob',
        timeout: 30000 // 30 second timeout
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    
    setCancelling(true);
    try {
      await axios.put(`${API}/bookings/${selectedBooking.id}/cancel`);
      toast.success('Booking cancelled successfully');
      setCancelDialogOpen(false);
      setSelectedBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const openCancelDialog = (booking) => {
    setSelectedBooking(booking);
    setCancelDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      sample_collected: 'bg-blue-50 text-blue-700 border-blue-100',
      processing: 'bg-purple-50 text-purple-700 border-purple-100',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      cancelled: 'bg-red-50 text-red-700 border-red-100'
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      initiated: 'bg-blue-50 text-blue-700 border-blue-100',
      paid: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      failed: 'bg-red-50 text-red-700 border-red-100',
      awaiting_confirmation: 'bg-purple-50 text-purple-700 border-purple-100'
    };
    const labels = {
      awaiting_confirmation: 'Awaiting Confirmation'
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  const filteredBookings = bookings.filter(booking => {
    // Status filter
    let matchesFilter = true;
    if (filter === 'upcoming') matchesFilter = ['pending', 'confirmed'].includes(booking.status);
    else if (filter === 'completed') matchesFilter = booking.status === 'completed';
    else if (filter === 'cancelled') matchesFilter = booking.status === 'cancelled';
    
    // Search filter
    if (!searchQuery) return matchesFilter;
    const query = searchQuery.toLowerCase();
    const testNames = getTestNames(booking.test_ids).toLowerCase();
    return matchesFilter && (
      testNames.includes(query) ||
      booking.id?.toLowerCase().includes(query)
    );
  });

  const getTestNames = (testIds) => {
    return testIds.map(id => tests[id]?.name || 'Unknown Test').join(', ');
  };

  const canCancelBooking = (booking) => {
    return ['pending', 'confirmed'].includes(booking.status) && booking.payment_status !== 'paid';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-bookings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Bookings</h1>
            <p className="text-muted-foreground mt-1">Track and manage your lab test appointments</p>
          </div>
          <Button 
            className="bg-sky-600 hover:bg-sky-700"
            onClick={() => navigate('/tests')}
            data-testid="book-new-test-btn"
          >
            <TestTubes className="w-4 h-4 mr-2" />
            Book New Test
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="card-clinical">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by test name or booking ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-bookings-input"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {['all', 'upcoming', 'completed', 'cancelled'].map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f)}
                  className={filter === f ? 'bg-sky-600 hover:bg-sky-700' : ''}
                  data-testid={`filter-${f}-btn`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No bookings found</h3>
            <p className="text-slate-600 mt-2">
              {filter === 'all' 
                ? "You haven't made any bookings yet" 
                : `No ${filter} bookings`}
            </p>
            <Button 
              className="mt-4 bg-sky-600 hover:bg-sky-700"
              onClick={() => navigate('/tests')}
            >
              Browse Tests
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <div 
                key={booking.id} 
                className="card-clinical hover:shadow-md transition-shadow"
                data-testid={`booking-card-${booking.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {getStatusBadge(booking.status)}
                      {getPaymentBadge(booking.payment_status)}
                      <span className="text-sm text-slate-500 mono">#{booking.id.slice(0, 8)}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">
                      {getTestNames(booking.test_ids)}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {booking.appointment_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {booking.appointment_time}
                      </span>
                      <span className="flex items-center gap-1">
                        {booking.booking_type === 'home_collection' ? (
                          <><Home className="w-4 h-4" /> Home Collection</>
                        ) : (
                          <><MapPin className="w-4 h-4" /> Walk-in</>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">
                        ₦{booking.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {booking.payment_status === 'pending' || booking.payment_status === 'initiated' ? (
                        <Button 
                          className="bg-sky-600 hover:bg-sky-700"
                          onClick={() => handlePayment(booking)}
                          data-testid={`pay-booking-${booking.id}-btn`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay Now
                        </Button>
                      ) : booking.payment_status === 'paid' ? (
                        <>
                          <Button 
                            variant="outline"
                            onClick={() => handleDownloadReceipt(booking.id)}
                            disabled={downloadingReceipt === booking.id}
                            data-testid={`download-receipt-${booking.id}-btn`}
                          >
                            {downloadingReceipt === booking.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 mr-2" />
                            )}
                            Receipt
                          </Button>
                          {booking.status === 'completed' && (
                            <Button 
                              variant="outline"
                              onClick={() => navigate('/results')}
                              data-testid={`view-results-${booking.id}-btn`}
                            >
                              View Results
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </>
                      ) : null}
                      
                      {/* Actions Menu */}
                      {canCancelBooking(booking) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`booking-actions-${booking.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => openCancelDialog(booking)}
                              className="text-red-600"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel Booking
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address for home collection */}
                {booking.booking_type === 'home_collection' && booking.address && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-600">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {booking.address}
                    </p>
                  </div>
                )}

                {/* Pending payment notice */}
                {booking.payment_status === 'pending' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Payment pending. Complete payment to confirm your booking.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Cancel Booking Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <X className="w-5 h-5" />
                Cancel Booking
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel this booking? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <div className="py-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-medium text-foreground mb-2">
                    {getTestNames(selectedBooking.test_ids)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedBooking.appointment_date} at {selectedBooking.appointment_time}
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Keep Booking
              </Button>
              <Button 
                variant="destructive"
                onClick={handleCancelBooking}
                disabled={cancelling}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Booking'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Method Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-sky-600" />
                Select Payment Method
              </DialogTitle>
              <DialogDescription>
                Choose how you'd like to pay for this booking
              </DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <div className="py-4 space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-medium text-foreground mb-1">
                    {getTestNames(selectedBooking.test_ids)}
                  </p>
                  <p className="text-2xl font-bold text-sky-600">
                    ₦{selectedBooking.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>

                {/* Payment Options */}
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedPaymentMethod('paystack')}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      selectedPaymentMethod === 'paystack' 
                        ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30' 
                        : 'border-border hover:border-sky-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🏦</span>
                        <div>
                          <p className="font-medium text-foreground">Paystack</p>
                          <p className="text-xs text-muted-foreground">Pay with card, bank, or USSD</p>
                        </div>
                      </div>
                      {selectedPaymentMethod === 'paystack' && (
                        <CheckCircle2 className="w-5 h-5 text-sky-600" />
                      )}
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedPaymentMethod('bank_transfer')}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      selectedPaymentMethod === 'bank_transfer' 
                        ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30' 
                        : 'border-border hover:border-sky-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🏛️</span>
                        <div>
                          <p className="font-medium text-foreground">Bank Transfer</p>
                          <p className="text-xs text-muted-foreground">Direct bank transfer</p>
                        </div>
                      </div>
                      {selectedPaymentMethod === 'bank_transfer' && (
                        <CheckCircle2 className="w-5 h-5 text-sky-600" />
                      )}
                    </div>
                  </button>
                </div>

                {/* Bank Transfer Details */}
                {selectedPaymentMethod === 'bank_transfer' && bankDetails && (
                  <div className="p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="w-5 h-5 text-sky-600" />
                      <h3 className="font-medium text-foreground">Bank Account Details</h3>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Bank Name:</span>
                        <span className="font-medium text-foreground">{bankDetails.bank_name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Account Number:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-foreground">{bankDetails.account_number}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(bankDetails.account_number)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Account Name:</span>
                        <span className="font-medium text-foreground">{bankDetails.account_name}</span>
                      </div>
                    </div>
                    <p className="text-xs text-sky-700 dark:text-sky-400 mt-3">
                      After transfer, contact support with your payment proof for confirmation.
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={processPayment}
                disabled={paymentProcessing || !selectedPaymentMethod}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="process-payment-btn"
              >
                {paymentProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : selectedPaymentMethod === 'bank_transfer' ? (
                  'Done'
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay Now
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MyBookings;
